import type { CustomFile } from '../types';

import { merge } from '../../../helper';

/*
 * Straight at the module, not through the utils/files barrel: that barrel
 * re-exports the file demo, which reaches back into the upload UI, so validating
 * a filename would drag the dropzone and the form prototypes in with it.
 */
import getPath from '../../files/getPath';
import createTimeSlice from '../../async/createTimeSlice';
import runWithConcurrency from '../../async/runWithConcurrency';

import { checkFile, compileRules } from './validate/checkFile';
import { toMessage } from './validate/errorMessages';
import { SKIP_FILE, type TypeFileOutcome, type TypeFileValidateResult, type TypeValidateFiles } from './validate/types';

// ----------------------------------------------------------------------------

export { SKIP_FILE } from './validate/types';

export type { TypeFileValidateResult, TypeFileValidationCustomResult, TypeValidateFiles } from './validate/types';

/*
 * A validator does real work per file, so it is worth overlapping; the built-in
 * checks are string comparisons, where a second runner only adds bookkeeping.
 */
const DEFAULT_CONCURRENCY = 20;
const DEFAULT_YIELD_EVERY_MS = 8;

// ----------------------------------------------------------------------------

async function validateFiles(files: CustomFile[], config: TypeValidateFiles = {}): Promise<TypeFileValidateResult> {
    // Default config
    const defConfig: TypeValidateFiles = {
        acceptUnknown: false,
        yieldEveryMs: DEFAULT_YIELD_EVERY_MS,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    const rules = compileRules(defConfig);

    /*
     * Read after the merge, not before it: a validator can arrive through the
     * inherited config, and settling the concurrency ahead of that left a caller
     * who passed one through `merge` validating a file at a time without asking to.
     */
    const hasCustomValidator = typeof defConfig.fnCustomValidator === 'function';
    const defaultConcurrency = hasCustomValidator ? DEFAULT_CONCURRENCY : 1;

    const concurrency = Math.max(1, Math.floor(defConfig.concurrency ?? defaultConcurrency));
    const slice = createTimeSlice(Math.max(0, defConfig.yieldEveryMs ?? DEFAULT_YIELD_EVERY_MS));

    // One outcome per file, by index, so the result keeps the order it arrived in.
    const outcomes = new Array<TypeFileOutcome | null>(files.length).fill(null);

    // ------------------------------------------------------------------------

    async function validateOne(file: CustomFile, i: number): Promise<void> {
        const filePath = getPath(file.name);

        file.baseName = filePath.baseName;
        file.extension = filePath.extension;

        const errors = checkFile(file, rules);

        // Custom validator
        if (typeof defConfig.fnCustomValidator === 'function') {
            try {
                const customResult = await defConfig.fnCustomValidator(file);

                if (customResult === SKIP_FILE) {
                    outcomes[i] = 'skipped';
                    await slice.maybeYield();
                    return;
                }

                if (customResult != null) {
                    if (Array.isArray(customResult)) {
                        errors.push(...customResult.filter((error): error is string => typeof error === 'string'));
                    } else if (typeof customResult === 'string') {
                        errors.push(customResult);
                    }
                }
            } catch (err) {
                errors.push(toMessage(err, 'Custom validator failed.'));
            }
        }

        /*
         * Save outcome. The same file failing two of the configured rules reads as
         * one reason to the user, so identical messages are not repeated.
         */
        outcomes[i] = errors.length === 0 ? 'accepted' : { errors: [...new Set(errors)] };

        await slice.maybeYield();
    }

    // ------------------------------------------------------------------------

    /*
     * One path whatever the concurrency, down to a single runner: the plain loop
     * this replaced ran the same worker with nothing catching for it, so a file
     * that threw took the entire validation down there while here it merely went
     * missing from the result. Now it is rejected, with the failure as its reason.
     *
     * Aborting only stops what has not started. A custom validator already in
     * flight is left to finish — there is no way to cancel one — and its outcome is
     * kept, since throwing it away would cost work already paid for.
     */
    await runWithConcurrency(files, concurrency, validateOne, {
        stopOnError: false,
        throwOnError: false,
        shouldStop: (): boolean => defConfig.signal?.aborted === true,
        onError: (err, _file, index): void => {
            outcomes[index] = { errors: [toMessage(err, 'Validation failed.')] };
        },
    });

    // ------------------------------------------------------------------------

    // Build final arrays in original order
    const accepted: CustomFile[] = [];
    const rejected: { file: CustomFile; errors: string[] }[] = [];
    const skipped: CustomFile[] = [];

    for (let i = 0; i < files.length; i++) {
        const outcome = outcomes[i];

        if (outcome === 'accepted') {
            accepted.push(files[i]);
            continue;
        }

        if (outcome === 'skipped') {
            skipped.push(files[i]);
            continue;
        }

        // Anything still null was never reached, which only an abort can do.
        if (outcome != null) {
            rejected.push({ file: files[i], errors: outcome.errors });
        }
    }

    return { accepted, rejected, skipped };
}

// ----------------------------------------------------------------------------

export default validateFiles;
